import { Check } from 'lucide-react';
import { siteConfig } from '@sighthost/config';

/**
 * TrustBlock — Hindsight affiliation copy + 4 trust bullets.
 *
 * Source of truth for affiliation copy: siteConfig.parent + siteConfig.affiliations
 * (existing fields per A8a.4 — do NOT add parent.tagline).
 *
 * The 4 bullets describe SightHost's own infrastructure / promise, NOT the
 * parent — kept inline (not in siteConfig) because they belong to this
 * surface specifically. If a second surface needs them later, hoist.
 *
 * variant:
 *   'full'    — desktop placement (left panel bottom). Multi-paragraph layout.
 *   'compact' — mobile placement (under the form). Tighter spacing, same content.
 */

interface TrustBlockProps {
  variant?: 'full' | 'compact';
}

export function TrustBlock({ variant = 'full' }: TrustBlockProps) {
  const parent = siteConfig.parent;
  const sister = siteConfig.affiliations[0];

  return (
    <div
      className={
        variant === 'full'
          ? 'mt-12 max-w-md space-y-4 border-t border-border pt-6'
          : 'space-y-3 rounded-md border border-border bg-card px-4 py-4'
      }
    >
      <p
        className={
          variant === 'full'
            ? 'font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground'
            : 'font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground'
        }
      >
        Trust
      </p>

      <p
        className={
          variant === 'full'
            ? 'text-sm leading-relaxed text-ink-soft'
            : 'text-xs leading-relaxed text-ink-soft'
        }
      >
        Part of{' '}
        <a
          href={parent.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-ink hover:text-primary hover:underline"
        >
          {parent.shortName}
        </a>{' '}
        — 10+ years building South African business software. We run{' '}
        <strong className="text-ink">{sister?.name}</strong> on this
        same infrastructure.
      </p>

      <ul className={variant === 'full' ? 'space-y-2' : 'space-y-1.5'}>
        <TrustBullet variant={variant}>POPIA compliant</TrustBullet>
        <TrustBullet variant={variant}>Cape Town POP</TrustBullet>
        <TrustBullet variant={variant}>1-hour first-response SLA</TrustBullet>
        <TrustBullet variant={variant}>No long-term contract</TrustBullet>
      </ul>
    </div>
  );
}

function TrustBullet({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant: 'full' | 'compact';
}) {
  return (
    <li
      className={
        variant === 'full'
          ? 'flex items-start gap-2 text-sm text-ink-soft'
          : 'flex items-start gap-2 text-xs text-ink-soft'
      }
    >
      <Check
        className={
          variant === 'full'
            ? 'mt-0.5 size-4 shrink-0 text-success'
            : 'mt-0.5 size-3.5 shrink-0 text-success'
        }
        aria-hidden
      />
      <span>{children}</span>
    </li>
  );
}
